import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useSubmitReflection } from "@/hooks/useMentorshipMeetings";
import { Loader2, Heart } from "lucide-react";

interface MentorshipReflectionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  requestId: string;
  mentorName: string;
}

export function MentorshipReflectionDialog({
  open,
  onOpenChange,
  requestId,
  mentorName,
}: MentorshipReflectionDialogProps) {
  const [reflection, setReflection] = useState("");
  const [thankYou, setThankYou] = useState("");
  const submitReflection = useSubmitReflection();

  const handleSubmit = async () => {
    await submitReflection.mutateAsync({
      requestId,
      reflection: reflection.trim() || undefined,
      thankYou: thankYou.trim() || undefined,
    });
    onOpenChange(false);
    setReflection("");
    setThankYou("");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Heart className="h-5 w-5 text-pink-500" />
            Share Your Thoughts
          </DialogTitle>
          <DialogDescription>
            {mentorName} has marked this mentorship as complete. Take a moment to reflect on your experience.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="reflection">What did you learn? (Optional)</Label>
            <Textarea
              id="reflection"
              placeholder="Share what you found valuable or what you'll apply..."
              value={reflection}
              onChange={(e) => setReflection(e.target.value)}
              className="resize-none"
              rows={3}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="thank-you">Thank-you note to {mentorName} (Optional)</Label>
            <Textarea
              id="thank-you"
              placeholder="A brief thank-you message..."
              value={thankYou}
              onChange={(e) => setThankYou(e.target.value)}
              className="resize-none"
              rows={3}
            />
            <p className="text-xs text-muted-foreground">
              {mentorName} will receive this note if you provide one.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Skip
          </Button>
          <Button onClick={handleSubmit} disabled={submitReflection.isPending}>
            {submitReflection.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Heart className="h-4 w-4 mr-2" />
            )}
            Submit
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
